<template>
  <div class="windscreen-claim">
    <shell ref="shell">
      <template #appbar>
        <app-bar
          :show-back="screen !== 'done'"
          subtitle="Windscreen claim · No assessment needed"
          @back="$emit('back')"
        />
      </template>

      <div v-if="screen === 'form'">
        <h2 class="mc-h2">Book your windscreen fitting</h2>
        <p class="mc-lede">
          Tell us where the car is and when suits you — an approved fitter comes
          to you, so there's no assessment to attend.
        </p>

        <div class="mc-row2">
          <div class="mc-field">
            <label class="mc-lbl" for="ws-reg"
              >Vehicle registration <span class="mc-req">*</span></label
            >
            <input
              id="ws-reg"
              type="text"
              autocapitalize="characters"
              placeholder="KDA 123X"
              :value="S.reg"
              @input="S.reg = $event.target.value.toUpperCase()"
            />
            <p class="mc-hint">We confirm your windscreen benefit from this.</p>
          </div>

          <div class="mc-field">
            <label class="mc-lbl" for="ws-phone"
              >Phone number <span class="mc-req">*</span></label
            >
            <input
              id="ws-phone"
              v-model="S.phone"
              type="tel"
              inputmode="tel"
              placeholder="07XX XXX XXX"
            />
            <p class="mc-hint">The fitter calls this number to confirm.</p>
          </div>
        </div>

        <fieldset class="mc-field mc-fieldset">
          <legend class="mc-lbl">
            Where should the fitter come? <span class="mc-req">*</span>
          </legend>
          <button
            type="button"
            class="mc-btn mc-btn--sec mc-wide"
            data-testid="ws-set-location"
            @click="sheetOpen = true"
          >
            📍 Set the location
          </button>
          <div v-if="S.location.locationName" class="mc-loc-card">
            <span>📍</span>
            <div class="mc-loc-text">{{ S.location.locationName }}</div>
            <button type="button" class="mc-linkbtn" @click="sheetOpen = true">
              Change
            </button>
          </div>
          <p v-else class="mc-hint">Home, work, or wherever the car is.</p>
        </fieldset>

        <fieldset class="mc-field mc-fieldset">
          <legend class="mc-lbl">
            Preferred day <span class="mc-req">*</span>
          </legend>
          <chip-group :options="DAYS" :value="S.day" @input="S.day = $event" />
        </fieldset>

        <div class="mc-field">
          <label class="mc-lbl" for="ws-time"
            >Preferred time <span class="mc-req">*</span></label
          >
          <input
            id="ws-time"
            v-model="S.time"
            type="time"
            :min="OPENS"
            :max="CLOSES"
          />
          <p v-if="timeOutOfHours" class="mc-err">
            Fitters work between 8:00am and 5:00pm. Pick a time in that window.
          </p>
          <p v-else class="mc-hint">
            Fitters work 8:00am to 5:00pm &mdash; they confirm the exact arrival
            time.
          </p>
        </div>

        <fieldset class="mc-field mc-fieldset">
          <legend class="mc-lbl">
            Photos of the car <span class="mc-req">*</span>
          </legend>
          <p class="mc-hint">
            One from the front, one from the rear — the fitter checks these
            before setting off.
          </p>
          <photo-doc-capture
            :items="PHOTOS"
            :captured="photos"
            @select="onCapture"
            @remove="onRemoveCapture"
            @error="(key, msg) => showToast(msg)"
          />
        </fieldset>

        <div class="mc-card-soft mc-mt24">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.9"
            stroke-linecap="round"
            class="mc-info-icon"
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 11v5" />
            <circle cx="12" cy="7.8" r=".9" fill="currentColor" stroke="none" />
          </svg>
          <div class="mc-info-copy">
            Only the windscreen damaged? You're in the right place. If the car
            has other damage, go back and submit a motor accident report
            instead.
          </div>
        </div>

        <div
          v-if="lookupFailed"
          class="mc-card-error mc-mt24"
          data-testid="lookup-error"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.9"
            stroke-linecap="round"
            class="mc-error-icon"
          >
            <path
              d="M10.3 3.9 2.5 18a1.9 1.9 0 0 0 1.7 2.8h15.6a1.9 1.9 0 0 0 1.7-2.8L13.7 3.9a1.9 1.9 0 0 0-3.4 0Z"
            />
            <path d="M12 9v4.2" />
            <circle cx="12" cy="16.9" r=".9" fill="currentColor" stroke="none" />
          </svg>
          <div class="mc-info-copy">
            <b>There was an error checking your information.</b> Please complete
            your claim through our back office &mdash; call
            <a href="tel:+254709912777">0709 912 777</a> or email
            <a href="mailto:claims@apainsurance.org">claims@apainsurance.org</a>.
          </div>
        </div>
      </div>

      <windscreen-done-step
        v-else
        :reg="S.reg"
        :phone="S.phone"
        :location-name="S.location.locationName"
        :when-label="whenLabel"
        :fitter="fitter"
      />

      <template #sheet>
        <bottom-sheet :open="sheetOpen" @close="sheetOpen = false">
          <location-picker
            v-if="sheetOpen"
            :value="S.location"
            title="Where should the fitter come?"
            @select="selectLocation"
            @cancel="sheetOpen = false"
            @error="showToast"
          />
        </bottom-sheet>
      </template>

      <template #bar>
        <bottom-bar>
          <template v-if="screen === 'done'">
            <button
              type="button"
              class="mc-btn mc-btn--pri"
              @click="$emit('back')"
            >
              Done
            </button>
          </template>
          <template v-else>
            <button
              type="button"
              class="mc-btn mc-btn--sec"
              @click="$emit('back')"
            >
              Back
            </button>
            <button
              type="button"
              class="mc-btn mc-btn--pri"
              data-testid="ws-submit"
              :disabled="!canSubmit || submitting"
              @click="submit"
            >
              {{ submitting ? 'Submitting...' : 'Submit windscreen claim' }}
            </button>
          </template>
        </bottom-bar>
      </template>

      <template #toast><toast ref="toast" /></template>
    </shell>
  </div>
</template>

<script>
import moment from 'moment'
import {
  initiateClaim,
  submitWindscreenClaim,
  uploadClaimAttachment,
} from '@/services/claim-service'
import Shell from '../shell/Shell.vue'
import AppBar from '../shell/AppBar.vue'
import BottomBar from '../shell/BottomBar.vue'
import BottomSheet from '../shell/BottomSheet.vue'
import Toast from '../shell/Toast.vue'
import ChipGroup from '../shared/ChipGroup.vue'
import LocationPicker from '../shared/LocationPicker.vue'
import PhotoDocCapture from '../shared/PhotoDocCapture.vue'
import WindscreenDoneStep from './WindscreenDoneStep.vue'
import {
  DRAFT_KEYS,
  saveDraft,
  loadDraft,
  clearDraft,
} from '@/utils/claimDraft'

const DAYS = ['Today', 'Tomorrow']
// Fitting hours, as the Nairobi wall clock the claimant is picking from.
// HH:mm sorts lexicographically, so the bounds compare as plain strings.
const OPENS = '08:00'
const CLOSES = '17:00'

// Front and rear only; keys match the accident journey's vehicle tiles.
const PHOTOS = [
  { key: 'front', label: 'Front', required: true },
  { key: 'rear', label: 'Rear', required: true },
]

const blankState = () => ({
  reg: '',
  phone: '',
  location: { latitude: null, longitude: null, locationName: '' },
  day: DAYS[0],
  time: '',
})

export default {
  name: 'WindscreenClaimForm',
  components: {
    Shell,
    AppBar,
    BottomBar,
    BottomSheet,
    Toast,
    ChipGroup,
    LocationPicker,
    PhotoDocCapture,
    WindscreenDoneStep,
  },
  emits: ['back'],
  data() {
    return {
      DAYS,
      PHOTOS,
      OPENS,
      CLOSES,
      screen: 'form',
      sheetOpen: false,
      submitting: false,
      lookupFailed: false,
      fitter: null,
      // { [key]: { name, type, url } } once uploaded. Outside S (the draft): a
      // stale S3 url would submit fine and point nowhere.
      photos: {},
      S: blankState(),
    }
  },
  computed: {
    canSubmit() {
      const digits = this.S.phone.replace(/\D/g, '')
      return Boolean(
        this.S.reg.trim() &&
          digits.length >= 9 &&
          this.S.location.locationName &&
          this.S.day &&
          this.S.time &&
          !this.timeOutOfHours &&
          !this.uploadingPhoto &&
          !this.missingPhotos.length
      )
    },
    // Mid-upload a photo has no url; the backend rejects one without.
    uploadingPhoto() {
      return Object.values(this.photos).some((photo) => photo.uploading)
    },
    missingPhotos() {
      return PHOTOS.filter((photo) => photo.required && !this.photos[photo.key])
    },
    timeOutOfHours() {
      return (
        Boolean(this.S.time) && (this.S.time < OPENS || this.S.time > CLOSES)
      )
    },
    whenLabel() {
      return `${this.S.day}${this.S.time ? ' at ' + this.S.time : ''}`
    },
  },
  watch: {
    S: {
      deep: true,
      handler() {
        if (this.screen === 'form') saveDraft(DRAFT_KEYS.windscreen, this.S)
      },
    },
  },
  created() {
    const draft = loadDraft(DRAFT_KEYS.windscreen)
    if (draft) this.S = { ...blankState(), ...draft }
  },
  methods: {
    showToast(message) {
      this.$refs.toast.show(message)
    },
    async onCapture(key, file) {
      this.photos = {
        ...this.photos,
        [key]: { name: file.name, uploading: true },
      }
      try {
        const { data } = await uploadClaimAttachment(
          file,
          this.S.reg,
          `photo_${key}`
        )
        if (!data.success) throw new Error('Attachment upload failed')
        this.photos = {
          ...this.photos,
          [key]: { name: file.name, type: data.data.type, url: data.data.url },
        }
      } catch {
        // don't leave the tile stuck at uploading
        this.onRemoveCapture(key)
        this.showToast('Could not upload that photo. Please try again.')
      }
    },
    onRemoveCapture(key) {
      const photos = { ...this.photos }
      delete photos[key]
      this.photos = photos
    },
    selectLocation(location) {
      this.S.location = location
      this.sheetOpen = false
      this.showToast('Location set')
    },
    async submit() {
      this.submitting = true
      this.lookupFailed = false
      try {
        const { data } = await initiateClaim(
          this.S.reg.trim(),
          this.S.phone.trim()
        )
        if (!data || !data.success) {
          this.lookupFailed = true
          return
        }

        const claimId = data.data && data.data.id

        // Map day 'Today' / 'Tomorrow' to YYYY-MM-DD format
        let formattedDate = moment().format('YYYY-MM-DD')
        if (this.S.day === 'Tomorrow') {
          formattedDate = moment().add(1, 'days').format('YYYY-MM-DD')
        }

        const submitPayload = {
          claimRecordId: claimId,
          regNo: this.S.reg.trim(),
          location: {
            longitude: this.S.location.longitude,
            latitude: this.S.location.latitude,
          },
          fittingTime: {
            day: formattedDate,
            time: this.S.time,
          },
          fittingLocation: this.S.location.locationName,
          attachments: Object.values(this.photos)
            .filter((photo) => photo && photo.url)
            .map(({ type, url }) => ({ type, url })),
        }

        const submitRes = await submitWindscreenClaim(submitPayload)
        if (!submitRes || !submitRes.data || !submitRes.data.success) {
          throw new Error(
            (submitRes && submitRes.data && submitRes.data.msg) ||
              'Submission failed'
          )
        }

        this.fitter = submitRes.data.data.fitter || null

        clearDraft(DRAFT_KEYS.windscreen)
        this.screen = 'done'
        this.$refs.shell.scrollToTop()
      } catch (err) {
        this.showToast(
          err.message || 'Could not submit your claim. Please try again.'
        )
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>

<style scoped>
.windscreen-claim {
  height: 100%;
}

.mc-h2 {
  font-size: 19px;
  line-height: 1.3;
  font-weight: 700;
  margin: 0 0 6px;
  color: #14142b;
}

.mc-lede {
  font-size: 14.5px;
  color: #6e7191;
  margin: 0 0 20px;
}

.mc-field {
  margin: 0 0 16px;
}

/* short fields pair up two-across at 900px+ instead of stretching solo
   across the wider content column */
@media (min-width: 900px) {
  .mc-row2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }

  .mc-row2 .mc-field {
    margin-bottom: 16px;
  }
}

/* the browser's default fieldset chrome would break the field rhythm */
.mc-fieldset {
  min-width: 0;
  padding: 0;
  border: 0;
}

.mc-lbl {
  display: block;
  padding: 0;
  font-size: 13px;
  font-weight: 600;
  color: #3d4450;
  margin-bottom: 7px;
}

.mc-req {
  color: #eb5757;
}

input {
  width: 100%;
  font-family: inherit;
  font-size: 16px;
  color: #333333;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 13px 14px;
  appearance: none;
  transition: border-color 0.15s, box-shadow 0.15s;
}

input:focus {
  outline: 0;
  border-color: #0066cc;
  box-shadow: 0 0 0 3px rgba(0, 102, 204, 0.13);
}

.mc-wide {
  width: 100%;
}

.mc-hint {
  font-size: 12.5px;
  color: #6e7191;
  margin: 6px 0 0;
}

.mc-err {
  font-size: 12.5px;
  font-weight: 600;
  color: #c1121f;
  margin: 6px 0 0;
}

.mc-loc-card {
  display: flex;
  gap: 11px;
  align-items: center;
  background: #eef3ff;
  border: 1px solid #bdd8f5;
  border-radius: 14px;
  padding: 15px;
  margin-top: 11px;
}

.mc-loc-text {
  flex: 1;
  min-width: 0;
  font-size: 13.5px;
  color: #002247;
  font-weight: 600;
}

.mc-linkbtn {
  border: 0;
  background: none;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  color: #0052b3;
  cursor: pointer;
  padding: 4px 0;
}

.mc-mt24 {
  margin-top: 24px;
}

.mc-card-soft {
  display: flex;
  gap: 11px;
  background: #eef3ff;
  border: 1px solid #bdd8f5;
  border-radius: 14px;
  padding: 16px;
}

.mc-info-icon {
  color: #0052b3;
  flex: 0 0 auto;
  margin-top: 1px;
}

.mc-info-copy {
  font-size: 12.5px;
  color: #002247;
  line-height: 1.5;
}

.mc-card-error {
  display: flex;
  gap: 11px;
  background: #fff5f5;
  border: 1px solid #f2c0c0;
  border-radius: 14px;
  padding: 16px;
}

.mc-error-icon {
  color: #c1121f;
  flex: 0 0 auto;
  margin-top: 1px;
}

.mc-card-error a {
  color: #c1121f;
  font-weight: 600;
  white-space: nowrap;
}
</style>
